import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { createNews, updateNews, getNewsById } from '../../services/newsService';
import { API_BASE_URL } from '../../config/api';
import './NewsInputPage.css';

const NewsInputPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Check canPostNews permission after subscription loads
  useEffect(() => {
    if (!subscriptionLoading && !hasPermission('canPostNews')) {
      setShowUpgradeModal(true);
    }
  }, [hasPermission, subscriptionLoading]);
  
  const [formData, setFormData] = useState({
    category: '',
    subCategory: '',
    title: '',
    excerpt: '',
    content: '',
    image: '',
    galleryImages: [],
    tags: [],
    status: 'draft',
    metaTitle: '',
    metaDescription: '',
    authorName: 'Admin'
  });

  const categories = [
    'Industry News',
    'Projects',
    'Building Materials',
    'Safety',
    'Sustainability',
    'Technology',
    'Infrastructure',
    'Real Estate'
  ];

  // Sub-categories mapping by category
  const subCategoriesMap = {
    'Industry News': ['Market Updates', 'Company News', 'Industry Events', 'Policy Changes'],
    'Projects': ['Commercial', 'Residential', 'Industrial', 'Government'],
    'Building Materials': ['Cements', 'Steel', 'Wood', 'Glass', 'Concrete'],
    'Safety': ['Equipment', 'Regulations', 'Training', 'Incidents'],
    'Sustainability': ['Green Building', 'Energy Efficiency', 'Recycling', 'LEED'],
    'Technology': ['Software', 'Equipment', 'Innovation', 'BIM'],
    'Infrastructure': ['Roads', 'Bridges', 'Railways', 'Utilities'],
    'Real Estate': ['Commercial', 'Residential', 'Investment', 'Development']
  };

  // Fetch news data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchNewsData();
    }
  }, [id]);

  const fetchNewsData = async () => {
    try {
      setLoading(true);
      const news = await getNewsById(id);
      setFormData({
        category: news.category || '',
        subCategory: news.subCategory || '',
        title: news.title || '',
        excerpt: news.excerpt || '',
        content: news.content || '',
        image: news.image || '',
        galleryImages: [],
        tags: news.tags ? JSON.parse(news.tags) : [],
        status: news.status || 'draft',
        metaTitle: news.metaTitle || '',
        metaDescription: news.metaDescription || '',
        authorName: news.authorName || 'Admin'
      });
    } catch (err) {
      setError('Failed to load news article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/upload/image?type=images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const data = await response.json();
      // Construct full URL for the uploaded image
      const baseUrl = API_BASE_URL.replace('/api', '');
      const imageUrl = `${baseUrl}${data.imageUrl || data.url}`;
      
      handleInputChange('image', imageUrl);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    handleInputChange('image', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (publishStatus = 'draft') => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }

    try {
      setSaving(true);
      const newsData = {
        ...formData,
        status: publishStatus
      };

      if (isEditMode) {
        await updateNews(id, newsData);
      } else {
        await createNews(newsData);
      }
      
      navigate('/admin/news');
    } catch (err) {
      setError(err.message || 'Failed to save news article');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (!formData.title.trim()) {
      setError('Please enter a title before proceeding');
      return;
    }
    if (currentStep < 2) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="news-input-loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="news-input-content">
        {/* Page Header */}
        <div className="ni-page-header">
          <div className="ni-page-title-section">
            <h1 className="ni-page-title">{isEditMode ? 'Edit News' : 'Create News'}</h1>
            <p className="ni-page-description">
              {isEditMode ? 'Update your news article' : 'Create a new news article for your website'}
            </p>
          </div>
          <div className="ni-header-actions">
            <button className="ni-cancel-btn" onClick={() => navigate('/admin/news')}>
              Cancel
            </button>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="ni-step-indicator">
          <div className="ni-step-container">
            <div className={`ni-step ${currentStep >= 1 ? 'completed' : ''}`}>
              <div className="ni-step-circle">1</div>
              <span className="ni-step-label">Basic Info</span>
            </div>
            <div className="ni-step-line"></div>
            <div className={`ni-step ${currentStep >= 2 ? 'completed' : ''}`}>
              <div className="ni-step-circle">2</div>
              <span className="ni-step-label">Publish</span>
            </div>
          </div>
        </div>

        {error && <div className="ni-error-message">{error}</div>}

        {/* Form Content */}
        <div className="ni-form-content">
          {currentStep === 1 && (
            <>
              {/* Category Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Category</h2>
                <div className="ni-form-card">
                  <div className="ni-form-row">
                    <div className="ni-form-group">
                      <label className="ni-form-label">Select Category *</label>
                      <select 
                        className="ni-select-field"
                        value={formData.category}
                        onChange={(e) => {
                          handleInputChange('category', e.target.value);
                          handleInputChange('subCategory', ''); // Reset sub-category when category changes
                        }}
                      >
                        <option value="">Select a category</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ni-form-group">
                      <label className="ni-form-label">Select Sub-Category</label>
                      <select 
                        className="ni-select-field"
                        value={formData.subCategory}
                        onChange={(e) => handleInputChange('subCategory', e.target.value)}
                        disabled={!formData.category}
                      >
                        <option value="">Select a sub-category</option>
                        {formData.category && subCategoriesMap[formData.category]?.map(subCat => (
                          <option key={subCat} value={subCat}>{subCat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Title Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Title</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">News Title *</label>
                    <input 
                      type="text" 
                      className="ni-text-input"
                      placeholder="Enter your news title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Excerpt Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Short Description</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Excerpt</label>
                    <textarea 
                      className="ni-textarea-input"
                      placeholder="Write a short summary of the news (displayed in listings)"
                      rows={3}
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange('excerpt', e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Main Image Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Main Image</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Upload Image</label>
                    
                    {!formData.image ? (
                      <div className="ni-upload-area">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleImageUpload}
                          className="ni-file-input"
                          id="news-image-upload"
                          disabled={uploading}
                        />
                        <label htmlFor="news-image-upload" className="ni-upload-label">
                          {uploading ? (
                            <span className="ni-uploading">
                              <span className="ni-spinner"></span>
                              Uploading...
                            </span>
                          ) : (
                            <>
                              <span className="ni-upload-icon">📷</span>
                              <span className="ni-upload-text">Click to upload image</span>
                              <span className="ni-upload-hint">JPEG, PNG, GIF or WebP (max 5MB)</span>
                            </>
                          )}
                        </label>
                      </div>
                    ) : (
                      <div className="ni-uploaded-image">
                        <div className="ni-image-preview">
                          <img 
                            src={formData.image} 
                            alt="Uploaded" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error';
                            }}
                          />
                        </div>
                        <button 
                          type="button" 
                          className="ni-remove-image-btn"
                          onClick={handleRemoveImage}
                        >
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Content</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Full Content *</label>
                    <textarea 
                      className="ni-textarea-input ni-content-area"
                      placeholder="Write the full news article content here..."
                      rows={10}
                      value={formData.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="ni-form-actions">
                <button className="ni-cancel-btn" onClick={() => navigate('/admin/news')}>
                  Cancel
                </button>
                <button className="ni-next-btn" onClick={handleNext}>
                  Next
                </button>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              {/* SEO Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">SEO Settings</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Meta Title</label>
                    <input 
                      type="text" 
                      className="ni-text-input"
                      placeholder="Enter meta title for SEO"
                      value={formData.metaTitle}
                      onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                    />
                  </div>
                  <div className="ni-form-group">
                    <label className="ni-form-label">Meta Description</label>
                    <textarea 
                      className="ni-textarea-input"
                      placeholder="Enter meta description for search engines"
                      rows={3}
                      value={formData.metaDescription}
                      onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Author Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Author</h2>
                <div className="ni-form-card">
                  <div className="ni-form-group">
                    <label className="ni-form-label">Author Name</label>
                    <input 
                      type="text" 
                      className="ni-text-input"
                      placeholder="Enter author name"
                      value={formData.authorName}
                      onChange={(e) => handleInputChange('authorName', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Preview Section */}
              <div className="ni-form-section">
                <h2 className="ni-section-title">Preview</h2>
                <div className="ni-preview-card">
                  <div className="ni-preview-image">
                    {formData.image ? (
                      <img 
                        src={formData.image} 
                        alt="News Preview" 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80';
                        }}
                      />
                    ) : (
                      <div className="ni-preview-placeholder">
                        <span>No image selected</span>
                      </div>
                    )}
                  </div>
                  <div className="ni-preview-content">
                    <span className="ni-preview-category">{formData.category || 'No category'}</span>
                    <h3 className="ni-preview-title">{formData.title || 'No title'}</h3>
                    <p className="ni-preview-excerpt">{formData.excerpt || formData.content?.substring(0, 150) + '...'}</p>
                    <span className="ni-preview-author">By {formData.authorName}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="ni-form-actions">
                <button className="ni-back-btn" onClick={handleBack}>
                  Back
                </button>
                <button 
                  className="ni-draft-btn" 
                  onClick={() => handleSubmit('draft')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                <button 
                  className="ni-publish-btn" 
                  onClick={() => handleSubmit('published')}
                  disabled={saving}
                >
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate(-1); // Go back when closing if no permission
        }}
        feature="Post News"
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="GCC Business"
      />
    </AdminLayout>
  );
};

export default NewsInputPage;
